import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import {
  APPLICATION_NAMES,
  DASHBOARD_ERROR_URL,
  DASHBOARD_URL,
  DeactivatableComponent,
  MANAGEMENT_URL,
  PENDING_URL,
  ROUTES_SUPPORT,
  SIGNUP_URL,
} from '@share/constants';
import { AuthenticationService } from '../../data-services/authentication/authentication.service';
import { InterceptorTypeEnum } from '../../data-services/authentication/InterceptorType.enum';

/**
 * A guard responsible for knowing if someone is authenticated for epm
 */

@Injectable()
export class AuthenticationGuard {
  private router = inject(Router);
  private authService = inject(AuthenticationService);

  private epmCompanyId: string;
  private user: string;
  private validUrls = [PENDING_URL, SIGNUP_URL, MANAGEMENT_URL];

  canDeactivate(
    component: DeactivatableComponent,
    _currentRoute: ActivatedRouteSnapshot,
    _currentState: RouterStateSnapshot,
    _nextState?: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    if (component && !component.canBeDeactivated()) {
      return confirm(
        'You have unsaved changes! If you leave, your changes will be lost.',
      );
    }
    return true;
  }

  /**
   * Checks the route to ensure a token is present and that the token's application matches the path the navigation
   * is going towards. For example if the application is EPM (a user is coming from epm) but has manipulated the url
   * to be /eyefinity/ instead of epm the guard will return false and redirect this to the dashboard error page. It
   * will also redirect if no token is found at all.
   * @param route
   * @param _state
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const result = this.hasToken(route);
    if (!result) {
      this.router.navigateByUrl(DASHBOARD_ERROR_URL);
    }

    if (
      route.url.some((segment) => segment.path === ROUTES_SUPPORT) &&
      !this.authService.isLoggedInAsEmployee()
    ) {
      this.router.navigateByUrl(DASHBOARD_URL);
      return;
    }

    return result;
  }

  hasToken(route): boolean {
    const token = this.authService.getPeConnAccessToken();
    const url = route && route._routerState ? route._routerState.url : '';
    const applicationUrl =
      token && token.application ? token.application.toLocaleLowerCase() : null;
    const hasCompanyId = !!token.companyId;
    const urlMatchesPath =
      url.includes(applicationUrl) || applicationUrl === APPLICATION_NAMES.pe;
    if (urlMatchesPath && applicationUrl === APPLICATION_NAMES.eyefinity) {
      this.authService.setInterceptorType(InterceptorTypeEnum.EYEFINITY);
      return true;
    } else if (
      urlMatchesPath &&
      (applicationUrl === APPLICATION_NAMES.epm ||
        applicationUrl === APPLICATION_NAMES.encompass) &&
      hasCompanyId
    ) {
      this.authService.setInterceptorType(InterceptorTypeEnum.EPM);
      return true;
    } else if (
      urlMatchesPath &&
      applicationUrl === APPLICATION_NAMES.pe &&
      hasCompanyId
    ) {
      this.authService.setInterceptorType(InterceptorTypeEnum.EPM);
      return true;
    } else {
      return false;
    }
  }
}
